import initAccordion from '../initAccordion';

document.addEventListener('DOMContentLoaded', (e) => {
  initAccordion();

  const root = document.querySelector('[data-calculator]');
  if (!root) return;

  /**
   * Модель, выбранная в соседнем блоке. Чтобы выбрать её снаружи:
   * document.dispatchEvent(new CustomEvent('nero:partner-model', { detail: 'pnl' }))
   */
  const MODEL_EVENT = 'nero:partner-model';

  /* ------------------------------------------------------------ условия */

  /** Базовая доля от спреда / комиссии / убытка клиента до множителей. */
  const BASE_RATE = { spread: 0.3, commission: 0.3, comm_spread: 0.2, pnl: 0.3 };

  /** Никакая комбинация множителей не даёт партнёру больше этой доли. */
  const RATE_CAP = 0.7;

  /** Чистый депозит между этими значениями линейно удваивает ставку. */
  const INOUT_FLOOR = 30_000;
  const INOUT_CEILING = 100_000;

  /** Бонус «на любой депозит» меньше этого размера ставку не меняет. */
  const BONUS_FREE_BELOW = 0.5;

  /** Порядок совпадает с порядком кнопок бонуса в разметке. */
  const BONUS_RATES = [0, 0.4, 0.5, 0.6, 1];

  /** Подсвеченная зона и позиции подписей под слайдерами. */
  const ZONES = { inout: [INOUT_FLOOR, INOUT_CEILING] };
  const MARKS = { inout: [0, INOUT_FLOOR, INOUT_CEILING, 200_000] };

  const state = {
    type: 'spread',
    comboBase: 'spread',
    spread: 10_000,
    commission: 10_000,
    pnl: -15_000,
    inout: 50_000,
    bonus: 0.4,
  };

  /* --------------------------------------------------------- форматирование */

  /** Разделители тысяч вручную: одинаковый результат во всех браузерах, минус — типографский. */
  function usd(value) {
    const rounded = Math.round(value);
    const digits = Math.abs(rounded)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return `${rounded < 0 ? '−' : ''}${digits} USD`;
  }

  /* ----------------------------------------------------------------- расчёт */

  function bonusMultiplier(bonus) {
    return bonus < BONUS_FREE_BELOW ? 1 : 1 - 0.5 * bonus;
  }

  function inoutMultiplier(inout) {
    const above = Math.min(INOUT_CEILING, inout) - Math.min(INOUT_FLOOR, inout);
    return 1 + above / (INOUT_CEILING - INOUT_FLOOR);
  }

  /** Наружу выходит только итог: базовая ставка и множители на странице не публикуются. */
  function compute(s) {
    if (s.type === 'fixed') return { mode: 'fixed' };

    const factor = bonusMultiplier(s.bonus) * inoutMultiplier(s.inout);
    const rateFor = (base) => Math.min(base * factor, RATE_CAP);
    const rebateAmount = (t) => (t === 'comm_spread' ? s.spread + s.commission : s[t]);

    if (s.type === 'spread' || s.type === 'commission' || s.type === 'comm_spread') {
      return { mode: 'amount', total: rebateAmount(s.type) * rateFor(BASE_RATE[s.type]) };
    }

    // Партнёру платят с того, что клиенты потеряли, поэтому знак меняется.
    const clientResult = -s.pnl;

    if (s.type === 'pnl') {
      return { mode: 'amount', total: clientResult * rateFor(BASE_RATE.pnl) };
    }

    // Combo: сначала ребейт, затем доля PnL с того, что осталось после него.
    const rebateReward = rebateAmount(s.comboBase) * rateFor(BASE_RATE[s.comboBase]);
    const pnlReward = (clientResult - rebateReward) * rateFor(BASE_RATE.pnl);
    return { mode: 'amount', total: rebateReward + pnlReward };
  }

  /* ----------------------------------------------------------------- трек */

  /**
   * Весь трек — один градиент, зона нарисована внутри него:
   *   заполнено, в зоне      — яркий акцент
   *   заполнено, вне зоны    — обычный акцент
   *   не заполнено, в зоне   — слабая акцентная заливка
   *   не заполнено, вне зоны — пустой желоб
   */
  function trackBackground(filledPct, zonePct) {
    const colorAt = (p) => {
      const inZone = zonePct ? p >= zonePct[0] && p <= zonePct[1] : false;
      if (p <= filledPct) return inZone ? 'var(--accent-soft)' : 'var(--accent)';
      return inZone ? 'rgba(var(--accent-glow), 0.22)' : 'rgba(255, 255, 255, 0.06)';
    };

    const bounds = [0, filledPct, ...(zonePct || []), 100].filter((p) => p >= 0 && p <= 100).sort((a, b) => a - b);

    const stops = [];
    for (let i = 0; i < bounds.length - 1; i += 1) {
      const from = bounds[i];
      const to = bounds[i + 1];
      if (to <= from) continue;
      const colour = colorAt((from + to) / 2);
      stops.push(`${colour} ${from}%`, `${colour} ${to}%`);
    }
    return `linear-gradient(to right, ${stops.join(', ')})`;
  }

  /* ------------------------------------------------------------- состояние */

  function set(key, value) {
    state[key] = value;
    render();
  }

  /* --------------------------------------------------------------- слайдеры */

  const sliders = Array.from(root.querySelectorAll('[data-slider]')).map((el) => {
    const key = el.dataset.slider;
    const range = el.querySelector('input[type="range"]');
    const num = el.querySelector('input[type="number"]');
    const min = Number(range.min);
    const max = Number(range.max);
    const toPct = (v) => ((v - min) / (max - min)) * 100;
    const zone = ZONES[key];
    const zonePct = zone ? [toPct(zone[0]), toPct(zone[1])] : undefined;

    // Каждая подпись стоит у своего значения: $30k на 15% трека, а не на трети.
    const marks = MARKS[key];
    if (marks) {
      el.querySelectorAll('[data-marks] > *').forEach((label, i) => {
        const at = toPct(marks[i]);
        label.style.left = `${at}%`;
        label.style.transform = at <= 0 ? 'none' : at >= 100 ? 'translateX(-100%)' : 'translateX(-50%)';
      });
    }

    const onInput = (e) => set(key, Number(e.target.value));
    range.addEventListener('input', onInput);
    num.addEventListener('input', onInput);

    return {
      key,
      sync(value) {
        range.value = String(value);
        // Не трогаем поле, пока введённое в нём уже равно значению (например, пустое при 0),
        // иначе курсор прыгает при наборе.
        if (Number(num.value) !== value) num.value = String(value);
        range.style.background = trackBackground(toPct(value), zonePct);
      },
    };
  });

  /* ------------------------------------------------------------ группы кнопок */

  const groups = Array.from(root.querySelectorAll('[data-group]'));

  groups.forEach((group) => {
    const key = group.dataset.group;
    group.querySelectorAll('button').forEach((btn, i) => {
      if (key === 'bonus') btn.dataset.value = String(BONUS_RATES[i]);
      btn.addEventListener('click', () => {
        set(key, key === 'bonus' ? Number(btn.dataset.value) : btn.dataset.value);
      });
    });
  });

  const pressedText = (key) => {
    const btn = root.querySelector(`[data-group="${key}"] button[aria-pressed="true"]`);
    return btn ? btn.textContent.trim() : '';
  };

  /* ---------------------------------------------------------------- отрисовка */

  const outputs = {};
  root.querySelectorAll('[data-out]').forEach((el) => {
    outputs[el.dataset.out] = el;
  });
  const conditional = root.querySelectorAll('[data-show]');

  function render() {
    const r = compute(state);
    const t = state.type;
    const cb = state.comboBase;
    const combo = t === 'combo';
    const total = r.mode === 'amount' ? r.total : 0;

    const flags = {
      combo,
      fixed: r.mode === 'fixed',
      amount: r.mode === 'amount',
      spread: t === 'spread' || t === 'comm_spread' || (combo && (cb === 'spread' || cb === 'comm_spread')),
      commission: t === 'commission' || t === 'comm_spread' || (combo && (cb === 'commission' || cb === 'comm_spread')),
      pnl: t === 'pnl' || combo,
      negative: r.mode === 'amount' && total < 0,
    };

    conditional.forEach((el) => {
      el.hidden = !flags[el.dataset.show];
    });

    groups.forEach((group) => {
      const current = String(state[group.dataset.group]);
      group.querySelectorAll('button').forEach((btn) => {
        btn.setAttribute('aria-pressed', String(btn.dataset.value === current));
      });
    });

    sliders.forEach((s) => s.sync(state[s.key]));

    outputs.total.textContent = usd(total);
    outputs.total.classList.toggle('is-negative', flags.negative);
    outputs.model.textContent = pressedText('type');
    outputs.bonus.textContent = pressedText('bonus');
    ['spread', 'commission', 'pnl', 'inout'].forEach((k) => {
      outputs[k].textContent = usd(state[k]);
    });
  }

  /* ---------------------------------------------------- выбор модели извне */

  document.addEventListener(MODEL_EVENT, (e) => {
    const key = e.detail;
    if (key) set('type', key);
  });

  render();
});
